// Metric and imperial display of the weight & balance figures. The system
// stores and computes in the aircraft's own metric units (kg, m, kgm) and
// fuel in US gallons; the imperial view is for comparing against the
// flight manual, which prints lb, inches, lb-in and US gallons.
//
// Conversion is for display only: nothing converted is ever stored or fed
// back into the pure W&B module except through unconvert().
//
// Pure; see navigation.ts.

import { LITRES_PER_USG } from './fuelPlan'

export const UNIT_SYSTEMS = ['metric', 'imperial'] as const
export type UnitSystem = (typeof UNIT_SYSTEMS)[number]
export const DEFAULT_UNIT_SYSTEM: UnitSystem = 'metric'

export const KG_PER_LB = 0.45359237
export const M_PER_IN = 0.0254

// What a figure is, in the unit the system holds it in.
//   mass    kg  -> lb
//   arm     m   -> in
//   moment  kgm -> lb-in
//   fuel    USG -> litres (the manual's gallons are the base unit)
export const UNIT_KINDS = ['mass', 'arm', 'moment', 'fuel'] as const
export type UnitKind = (typeof UNIT_KINDS)[number]

interface Spec {
  /** Display units per base unit, in the imperial system. */
  imperial: number
  /** ... and in the metric system. */
  metric: number
  digits: Record<UnitSystem, number>
  label: Record<UnitSystem, string>
}

const SPECS: Record<UnitKind, Spec> = {
  mass: { metric: 1, imperial: 1 / KG_PER_LB, digits: { metric: 1, imperial: 0 }, label: { metric: 'kg', imperial: 'lb' } },
  arm: { metric: 1, imperial: 1 / M_PER_IN, digits: { metric: 3, imperial: 2 }, label: { metric: 'm', imperial: 'in' } },
  moment: {
    metric: 1,
    imperial: 1 / (KG_PER_LB * M_PER_IN),
    digits: { metric: 1, imperial: 0 },
    label: { metric: 'kgm', imperial: 'lb·in' },
  },
  // Gallons are the stored unit, so the imperial view is the stored figure.
  fuel: { metric: LITRES_PER_USG, imperial: 1, digits: { metric: 1, imperial: 1 }, label: { metric: 'l', imperial: 'USG' } },
}

/** The stored figure in the system's unit. */
export function convert(kind: UnitKind, value: number, system: UnitSystem): number {
  return value * SPECS[kind][system]
}

/** A figure typed in the system's unit, back to the stored one. */
export function unconvert(kind: UnitKind, value: number, system: UnitSystem): number {
  return value / SPECS[kind][system]
}

export function unitLabel(kind: UnitKind, system: UnitSystem): string {
  return SPECS[kind].label[system]
}

/** Fuel flow's unit: litres or US gallons per hour. */
export function fuelFlowLabel(system: UnitSystem): string {
  return `${unitLabel('fuel', system)}/h`
}

/** The figure in the system's unit, fixed to its usual decimals (or `digits`). */
export function formatUnit(kind: UnitKind, value: number, system: UnitSystem, digits?: number): string {
  return convert(kind, value, system).toFixed(digits ?? SPECS[kind].digits[system])
}

export function isUnitSystem(value: unknown): value is UnitSystem {
  return value === 'metric' || value === 'imperial'
}

/**
 * The factors the worked weight & balance sheet prints, so the pilot can redo
 * every conversion by hand. `density` is the fuel's kg per litre.
 */
export function conversionFactors(density: number) {
  return {
    lbPerKg: 1 / KG_PER_LB,
    kgPerLb: KG_PER_LB,
    litresPerUsg: LITRES_PER_USG,
    usgPerLitre: 1 / LITRES_PER_USG,
    inchesPerMetre: 1 / M_PER_IN,
    metresPerInch: M_PER_IN,
    lbInPerKgm: 1 / (KG_PER_LB * M_PER_IN),
    densityKgPerLitre: density,
    densityKgPerUsg: density * LITRES_PER_USG,
    densityLbPerUsg: (density * LITRES_PER_USG) / KG_PER_LB,
  }
}
